import React from 'react';

import styled from 'styled-components';

import { APIUserType } from '@cd3p/core/types/api';
import { Box, Typography } from 'components';

const StyledHeader = styled(Typography)`
  font-weight: 900;
  line-height: 125%;
  text-transform: uppercase;
  margin-bottom: 1rem;
  margin-top: 1rem;
  .anchor {
    display: none;
  }
`;

const StyledHeaderH1 = styled(StyledHeader)`
  color: ${props => props.theme.custom.palette.secondary700};
  font-size: 2rem;
`;

const StyledHeaderH2 = styled(StyledHeader)`
  color: ${props => props.theme.custom.palette.secondary500};
  font-size: 1.6rem;
`;

const StyledHeaderH3 = styled(StyledHeader)`
  color: ${props => props.theme.custom.palette.primary900};
  font-size: 1.3rem;
`;

type MdxProps = {
  children: React.ReactNode[];
};

const h1: React.FC<MdxProps> = ({ children }) => (
  <StyledHeaderH1>{children}</StyledHeaderH1>
);

const h2: React.FC<MdxProps> = ({ children }) => (
  <StyledHeaderH2>{children}</StyledHeaderH2>
);

const h3: React.FC<MdxProps> = ({ children }) => (
  <StyledHeaderH3>{children}</StyledHeaderH3>
);

const StyledParagraph = styled(Typography)`
  color: ${props => props.theme.custom.palette.darkText};
  font-size: 1.4rem;
  line-height: 2.3rem;
  font-weight: 400;
  letter-spacing: -0.005em;
  white-space: pre-line;
  padding: 0;
`;

const p: React.FC<MdxProps> = ({ children }) => (
  <StyledParagraph variant="body1">{children}</StyledParagraph>
);

const StyledOL = styled.ol`
  list-style: decimal;
  padding-left: 2rem;
  font-size: 1.5rem;
  line-height: 2.3rem;
  font-weight: normal;
  letter-spacing: -0.005em;
  margin-bottom: 2rem;

  > li {
    color: ${props => props.theme.custom.palette.darkText};
    font-weight: 400;
    font-size: 1.4rem;
    line-height: 2.3rem;
    font-family: Source Sans Pro, serif;
  }
`;

const StyledUl = styled.ul`
  list-style: disc;
  padding-left: 2rem;
  font-size: 1.5rem;
  line-height: 2.3rem;
  font-weight: normal;
  letter-spacing: -0.005em;
  margin-bottom: 2rem;
  > li {
    color: ${props => props.theme.custom.palette.darkText};
    font-weight: 400;
    font-size: 1.4rem;
    line-height: 2.3rem;
    font-family: Source Sans Pro, serif;
  }
  > li > p {
    margin: 0;
    padding: 0;
  }
`;

const em: React.FC<MdxProps> = ({ children }) => (
  <em style={{ fontStyle: 'italic' }}>{children}</em>
);

const ol: React.FC<MdxProps> = ({ children }) => (
  <StyledOL>{children}</StyledOL>
);

const ul: React.FC<MdxProps> = ({ children }) => (
  <StyledUl>{children}</StyledUl>
);

const img: React.FC<any> = props => (
  <Box
    component="img"
    sx={{
      maxHeight: 500,
      maxWidth: 550,
    }}
    {...props}
  />
);

export const docsComponents = {
  h1: h1,
  h2: h2,
  h3: h3,
  p: p,
  ul: ul,
  ol: ol,
  em: em,
  img: img,
};

export const getTagRegExp = (tagName: string) =>
  new RegExp(`<${tagName}>(.*?)</${tagName}>`, 'gs');

export const filterContentByUserType = (
  text: string | null | undefined,
  userType: APIUserType | undefined,
  highlight?: string | null,
) => {
  if (!text) return '';

  text = text.replace(
    getTagRegExp('center'),
    '<p style="text-align: center;"> $1 </p>',
  );

  text = text.replace(
    /```([\s\S]*?)```/g,
    '<pre style="font-family: monospace; background-color: #f8f9fa; padding: 16px; border-radius: 6px;overflow-x: auto; border: 1px solid #e9ecef;">$1</pre>',
  );

  text = text.replace(
    /`([^`]+)`/g,
    '<span style="font-family: monospace; background-color: #f8f9fa; padding: 2px 4px; border-radius: 3px; font-size: 0.9em;">$1</span>',
  );

  if (highlight) {
    const highlightRegexp = new RegExp(highlight, 'ig');
    text = text.replace(highlightRegexp, '<mark>$&</mark>');
  }

  if (userType === APIUserType.Dispatcher || userType === APIUserType.Support) {
    return text
      .replace(getTagRegExp('contractor'), '')
      .replace(getTagRegExp('dispatcher'), '$1');
  } else if (userType === APIUserType.Contractor) {
    return text
      .replace(getTagRegExp('dispatcher'), '')
      .replace(getTagRegExp('contractor'), '$1');
  } else {
    return '';
  }
};
